import { useCallback, useMemo } from "react";
import { useQuery } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import {
  getEffectiveLevel,
  type ChannelOverride,
} from "@/lib/channel-notification-overrides";
import { findKeywordMatches } from "@/lib/notification-keywords";

interface NotificationEvent {
  id: string;
  channel_id: string;
  workspace_id?: string | null;
  reason: string;
  body: string;
}

/**
 * Hook that queries notification preferences, channel overrides, DND settings,
 * and keywords via Zero and exposes a shouldShowNotification callback.
 *
 * Evaluates per-event workspace context instead of relying on a single
 * selected workspace. This allows correct filtering when events arrive
 * from different workspaces (e.g. Home sidebar, global notifications).
 */
export function useNotificationFilter(userId: string | undefined) {
  // Single composite that consolidates the four user-scoped settings
  // tables (preferences + channel-overrides + dnd + keywords). Cuts
  // four always-on Zero hubs to one. Slice deps are destructured so an
  // update to one relation (e.g. a new keyword) doesn't churn the
  // memos that read another (e.g. preferences). Mirrors the
  // useTeamMemberAndAgentData / MessageArea byIdComposite pattern.
  const [composite] = useQuery(
    userId ? queries.users.notificationSettingsComposite() : null,
  );
  const compositePreferences = composite?.notification_preferences;
  const compositeOverrides = composite?.channel_notification_overrides;
  const compositeDnd = composite?.dnd_settings;
  const compositeKeywords = composite?.notification_keywords;

  const preferencesById = useMemo(() => {
    const map = new Map<
      string,
      {
        desktop_enabled: boolean;
        global_level: string;
      }
    >();
    for (const pref of compositePreferences ?? []) {
      map.set(pref.workspace_id, pref);
    }
    return map;
  }, [compositePreferences]);

  const channelOverridesById = useMemo(() => {
    const map = new Map<string, ChannelOverride>();
    for (const override of compositeOverrides ?? []) {
      map.set(override.channel_id, override as ChannelOverride);
    }
    return map;
  }, [compositeOverrides]);

  // dnd_settings was a `.one()` query and produced a single row. The
  // `.related("dnd_settings")` relation returns an array; pull the
  // first row (there's at most one per user) so downstream callers
  // don't need to re-shape.
  const dndSettings = useMemo(() => compositeDnd?.[0] ?? null, [compositeDnd]);

  const keywordsByWorkspace = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const kw of compositeKeywords ?? []) {
      const existing = map.get(kw.workspace_id) ?? [];
      existing.push(kw.keyword);
      map.set(kw.workspace_id, existing);
    }
    return map;
  }, [compositeKeywords]);

  const shouldShowNotification = useCallback(
    (event: NotificationEvent): boolean => {
      // 1. Check DND (global, not per-workspace)
      if (dndSettings) {
        if (isDndActive(dndSettings)) {
          return false;
        }
      }

      // 2. Resolve workspace preferences for this event's workspace
      const wsId = event.workspace_id;
      const preferences = wsId ? preferencesById.get(wsId) : undefined;

      const desktopEnabled = preferences?.desktop_enabled !== false;
      const globalLevel = preferences?.global_level ?? "mentions_dms";

      // 3. Check desktop_enabled
      if (!desktopEnabled) {
        return false;
      }

      // 4. Global "nothing" suppresses all
      if (globalLevel === "nothing") {
        return false;
      }

      // 5. Get effective level for this channel
      const channelOverride = channelOverridesById.get(event.channel_id);
      const effectiveLevel = getEffectiveLevel(
        globalLevel,
        channelOverride ?? null,
        "desktop",
      );

      const isDM = event.reason === "dm";
      const isMention =
        event.reason === "mention" || event.reason === "direct_mention";

      // 6. Apply channel-level decision
      if (effectiveLevel === "mute") {
        return isMention;
      }

      if (effectiveLevel === "mentions") {
        if (isDM || isMention) return true;
        // Check keyword promotion for channel messages
        if (event.reason === "channel" && wsId && event.body) {
          const keywordStrings = keywordsByWorkspace.get(wsId) ?? [];
          if (keywordStrings.length > 0) {
            const matches = findKeywordMatches(event.body, keywordStrings);
            if (matches.length > 0) return true;
          }
        }
        return false;
      }

      // effectiveLevel === "all" → notify for everything
      return true;
    },
    [dndSettings, preferencesById, channelOverridesById, keywordsByWorkspace],
  );

  return { shouldShowNotification };
}

export function isDndActive(dnd: {
  enabled: boolean;
  start_time?: string | null;
  end_time?: string | null;
  until?: number | null;
}): boolean {
  if (!dnd.enabled) return false;

  // If "until" is set and in the future, DND is active
  if (dnd.until) {
    return Date.now() < dnd.until;
  }

  // If start_time/end_time are set, check schedule
  if (dnd.start_time && dnd.end_time) {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const [startH, startM] = dnd.start_time.split(":").map(Number);
    const [endH, endM] = dnd.end_time.split(":").map(Number);
    const startMinutes = startH * 60 + startM;
    const endMinutes = endH * 60 + endM;

    if (startMinutes <= endMinutes) {
      return currentMinutes >= startMinutes && currentMinutes < endMinutes;
    }
    // Overnight range (e.g., 22:00 - 08:00)
    return currentMinutes >= startMinutes || currentMinutes < endMinutes;
  }

  // enabled with no schedule/until = always on
  return true;
}
